import {
  KnownProjectTeamMemberDtoStatus,
  KnownProjectTeamMemberDtoInvitationStatus,
  ProjectTeamMemberDto,
  KnownSortOrder
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback } from 'react';
import { createPath, ProjectPathParams, Paths } from '../../../../../app/router/app.router';
import { TableComponents } from '../../../../../ui/components/pure/list-table/list-table-cells.component';
import { ListTable } from '../../../../../ui/components/pure/list-table/list-table.component';
import { initTable } from '../../../../../ui/components/pure/list-table/list-table.init';
import { pinned } from '../../../../../ui/components/pure/list-table/list-table-column-styles';
import { TeamMembersListControlsState } from '../../../team.queries';

interface TeamMembersListTableProps {
  params: ProjectPathParams<Paths.team>;
  rows: ProjectTeamMemberDto[];
  setFormData: (update: Partial<TeamMembersListControlsState>) => void;
  sortData: { sortBy: string; sortOrder: KnownSortOrder };
  isLoading: boolean;
  isPreviousData: boolean;
  hasError: boolean;
}

// Name and role are free text and share the spare width; access level and account status are
// fixed-format values, so they keep one width at every screen size.
const FIXED_COLUMN_WIDTHS = {
  accessLevel: '160px',
  accountStatus: '220px'
} as const;

const tb = initTable<ProjectTeamMemberDto>();
tb.styles({
  table: { tableLayout: 'fixed' }
});

tb.column('NAME', TableComponents.BoldText, {
  value: (row) => `${row.firstName} ${row.lastName}`
}).options({
  isSortable: true,
  sortId: 'firstName',
  minBreakpoint: 'md'
});
tb.column('ROLE', TableComponents.Text, { value: ':role' }).options({
  isSortable: true,
  sortId: 'role',
  minBreakpoint: 'md'
});
tb.column('ACCESS LEVEL', TableComponents.Text, {
  value: ':accessLevel'
}).options({
  isSortable: true,
  sortId: 'accessLevel',
  minBreakpoint: 'md',
  headerStyles: pinned(FIXED_COLUMN_WIDTHS.accessLevel)
});
tb.column('ACCOUNT STATUS', TableComponents.Text, {
  value: (row) =>
    `${row.status}${
      row.invitationStatus &&
      row.status !== row.invitationStatus &&
      row.invitationStatus !== KnownProjectTeamMemberDtoInvitationStatus.Accepted
        ? ` (Invite ${row.invitationStatus.toLowerCase()})`
        : ''
    }`
}).options({
  isSortable: true,
  sortId: 'status',
  minBreakpoint: 'md',
  headerStyles: pinned(FIXED_COLUMN_WIDTHS.accountStatus)
});

tb.columnGroup('NAME')
  .row(TableComponents.BoldText, {
    value: (row) => `${row.firstName} ${row.lastName}`
  })
  .row(TableComponents.Text, { value: ':role' })
  .options({ isSortable: true, sortId: 'firstName', maxBreakpoint: 'sm' });
tb.columnGroup('')
  .row(TableComponents.Text, { value: ':accessLevel' })
  .row(TableComponents.Text, {
    value: (row) =>
      `${row.status}${
        row.invitationStatus &&
        row.status !== row.invitationStatus &&
        row.invitationStatus !== KnownProjectTeamMemberDtoInvitationStatus.Accepted
          ? ` (Invite ${row.invitationStatus.toLowerCase()})`
          : ''
      }`
  })
  .options({ isSortable: false, maxBreakpoint: 'sm' });

tb.rowStyles({
  a: (row) => ({
    color: `${row.status === KnownProjectTeamMemberDtoStatus.Invited && '#ADADAD'}`,
    textDecoration:
      row.status === KnownProjectTeamMemberDtoStatus.Suspended ||
      row.status === KnownProjectTeamMemberDtoStatus.Removed ||
      row.invitationStatus === KnownProjectTeamMemberDtoInvitationStatus.Cancelled
        ? 'line-through'
        : undefined
  }),
  td: {
    height: '64px'
  }
});

export function TeamMembersListTable({
  params,
  isLoading,
  isPreviousData,
  rows,
  setFormData,
  sortData,
  hasError
}: TeamMembersListTableProps) {
  const mapRowURL = useCallback(
    (row: ProjectTeamMemberDto) =>
      createPath(Paths.teamMember, { projectID: params.shortcode || params.projectID, teamMemberID: row.accountID }),
    [params.projectID, params.shortcode]
  );

  tb.config({ mapRowURL });

  return (
    <ListTable
      uniqueKey="accountID"
      onSort={setFormData}
      sortData={sortData}
      rows={rows}
      table={tb}
      isLoading={isLoading}
      isPreviousData={isPreviousData}
      hasError={hasError}
    />
  );
}
